import moment from 'moment-timezone'

/**
 * Convert selected date + UTC slot time to UTC timestamp
 * This ensures the date selected by the user is preserved when converting to UTC
 * @param {string} selectedDateStr - Date string in YYYY-MM-DD format
 * @param {string} slotStartUTC - UTC timestamp string
 * @param {string} slotEndUTC - Optional UTC timestamp string
 * @param {string} clientTimezone - User's timezone
 * @returns {Object} Object with start and end moment objects
 */
export const convertDateAndSlotToUTC = (
  selectedDateStr,
  slotStartUTC,
  slotEndUTC = null,
  clientTimezone,
) => {
  if (!selectedDateStr || !slotStartUTC || !clientTimezone) {
    return { start: null, end: null }
  }

  // Convert the UTC slot time to user's timezone to get the displayed time
  const slotInUserTimezone = moment.utc(slotStartUTC).tz(clientTimezone)
  const timeOfDay = {
    hour: slotInUserTimezone.hour(),
    minute: slotInUserTimezone.minute(),
    second: slotInUserTimezone.second(),
  }

  // Combine selected date with the displayed time in user's timezone
  // Then convert back to UTC for the API
  const selectedDateInTimezone = moment.tz(selectedDateStr, clientTimezone)
  const slotStartInUTC = moment
    .tz(
      [
        selectedDateInTimezone.year(),
        selectedDateInTimezone.month(),
        selectedDateInTimezone.date(),
        timeOfDay.hour,
        timeOfDay.minute,
        timeOfDay.second || 0,
      ],
      clientTimezone,
    )
    .utc()

  let slotEndInUTC = null
  if (slotEndUTC) {
    // Convert the UTC slot end time to user's timezone
    const slotEndInUserTimezone = moment.utc(slotEndUTC).tz(clientTimezone)
    const endTimeOfDay = {
      hour: slotEndInUserTimezone.hour(),
      minute: slotEndInUserTimezone.minute(),
      second: slotEndInUserTimezone.second(),
    }

    // Combine selected date with the displayed end time in user's timezone
    slotEndInUTC = moment
      .tz(
        [
          selectedDateInTimezone.year(),
          selectedDateInTimezone.month(),
          selectedDateInTimezone.date(),
          endTimeOfDay.hour,
          endTimeOfDay.minute,
          endTimeOfDay.second || 0,
        ],
        clientTimezone,
      )
      .utc()
  }

  return {
    start: slotStartInUTC,
    end: slotEndInUTC,
  }
}

/**
 * Get initial date for reschedule flow
 * @param {string} schedulingType - Type of scheduling
 * @param {Object} scheduledSession - Existing session data
 * @param {string} defaultTimezone - Default timezone
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const getInitialDate = (
  schedulingType,
  scheduledSession,
  defaultTimezone,
) => {
  if (schedulingType === 'reschedule' && scheduledSession?.slotStart) {
    // Convert UTC timestamp to user's timezone to get the date
    const tz = defaultTimezone
    return moment.utc(scheduledSession.slotStart).tz(tz).format('YYYY-MM-DD')
  }
  return moment().format('YYYY-MM-DD')
}

/**
 * Get initial slot for reschedule flow
 * @param {string} schedulingType - Type of scheduling
 * @param {Object} scheduledSession - Existing session data
 * @returns {Object|null} Slot object with start and end, or null
 */
export const getInitialSlot = (schedulingType, scheduledSession) => {
  if (
    schedulingType === 'reschedule' &&
    scheduledSession?.slotStart &&
    scheduledSession?.slotEnd
  ) {
    return {
      start: scheduledSession.slotStart,
      end: scheduledSession.slotEnd,
    }
  }
  return null
}
